import { useCallback } from "react";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { Grouping } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ItemDataType } from "../../../../../../../../../api/types/svTypes.ts";
import CustomLabel from "../../../../viewer/common/fields/CustomLabel.tsx";
import FieldOptionDeleteButton from "../../../../viewer/common/fields/FieldOptionDeleteButton.tsx";
import FieldOptionsDivider from "../../../../viewer/common/fields/FieldOptionsDivider.tsx";
import FieldPopover from "../../../../viewer/common/fields/FieldPopover.tsx";
import SuppressBlankValues from "../../../common/fields/field-options/SuppressBlankValues.tsx";
import type { GeneralFieldOptionPopupProps } from "../../../common/fields/types/generalFieldOptionPopup.types.ts";
import { isReferenceDateField } from "../../../common/utilities/isReferenceDateField.ts";
import DateGroupingComponent from "../../../tabular/table/DateGroupingComponent.tsx";

interface Props extends GeneralFieldOptionPopupProps {
  supportedDateGroupings?: (Grouping | null)[];
}

export default function GeneralFieldOptionPopup({
  anchorEl,
  field,
  supportedDateGroupings,
  cancel,
  onUpdateConfiguration,
  onRemove,
  onUpdateCustomLabel,
  readonly,
}: Props) {
  const isDateField = field.meta.type === ItemDataType.Date;
  const isNumeric = field.meta.type === ItemDataType.Numeric;
  const showGroupings = isReferenceDateField(field.meta);

  const updateGrouping = useCallback(
    (newGrouping: Grouping | null) => {
      if (field.config.grouping !== newGrouping) {
        onUpdateConfiguration({ grouping: newGrouping }, true);
      }
    },
    [field.config.grouping, onUpdateConfiguration]
  );

  const handleSuppressValues = useCallback(
    (changes: Partial<FieldConfiguration>) => {
      onUpdateConfiguration(changes, false);
    },
    [onUpdateConfiguration]
  );

  const handleOnRemove = useCallback(() => {
    onRemove();
    cancel();
  }, [onRemove, cancel]);

  return (
    <FieldPopover open={!!anchorEl} anchorEl={anchorEl} onClose={cancel} sx={{ width: 400, py: 0 }}>
      <CustomLabel
        customFieldValue={field.config.customLabel}
        originalFieldValue={field.meta.caption}
        updateCustomFieldValue={onUpdateCustomLabel}
        readonly={readonly}
      />
      <FieldOptionsDivider />
      {isDateField && showGroupings && (
        <DateGroupingComponent
          field={field}
          supportedDateGroupings={supportedDateGroupings || defaultDateGroupingForColumns}
          updateGrouping={updateGrouping}
          readonly={readonly}
        />
      )}
      {!isDateField && (
        <SuppressBlankValues
          field={field}
          onChanged={handleSuppressValues}
          showExcludeZeroValues={field.config.hideBlankValues === true && isNumeric}
          readonly={readonly}
        />
      )}
      <FieldOptionsDivider />
      {!readonly && <FieldOptionDeleteButton onRemove={handleOnRemove} />}
    </FieldPopover>
  );
}

const defaultDateGroupingForColumns = [Grouping.ByYear, Grouping.ByQuarter, Grouping.ByMonth];
